/**
 * ProductRow - Clean, refactored product row component
 *
 * Replaces the duplicated column rendering logic in RenderRow with a simple
 * column-driven approach. Each column type is rendered once via a lookup.
 * Uses centralized styles from styles/utilities/view-agreement.ts
 */

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import NewRenderCell from '../Deals/grid/cells/NewRenderCell'
import {
  COMPACT_BREAKPOINT,
  CellWrapper,
  ProductRowContainer,
  RowNumberBadge,
  getGridSizes,
} from '../ProductRows/ProductTableStyles'
import { columns, csaColumns, ioColumns } from './columns'
import ServiceDetails from './ServiceDetails'

// Column IDs that should be visible on small screens
const ALWAYS_VISIBLE_IDS = ['productName', 'description', 'price', 'deleteButton']

// Column IDs that should render (excludes internal fields)
const SKIP_COLUMN_IDS = ['rowId']

interface ProductRowProps {
  row: any
  index: number
  handleProductChange: (index: number, field: string, value: any, extra?: any) => void
  handleRemoveRow: (sk: string) => void
  validationErrors: any
  type: string
  isDraft: boolean
  customByServiceKey?: Map<string, { price: number; margin: number }>
  editableFields?: Set<string>
}

const ProductRow: React.FC<ProductRowProps> = ({
  row,
  index,
  handleProductChange,
  handleRemoveRow,
  validationErrors,
  type,
  isDraft,
  customByServiceKey,
  editableFields,
}) => {
  const theme = useTheme()
  // Use centralized breakpoint for compact view threshold
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(COMPACT_BREAKPOINT))
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Select columns based on deal type
  const csaTypes = ['CSA', 'RCSA']
  const ioTypes = ['IO']
  const rowColumns = csaTypes.includes(type) ? csaColumns : ioTypes.includes(type) ? ioColumns : columns

  // Filter columns for visibility
  const visibleColumns = rowColumns.filter(col => {
    if (SKIP_COLUMN_IDS.includes(col.id)) return false
    // Hide delete button unless product changes are allowed (productName in editableFields)
    if (editableFields && col.id === 'deleteButton' && !editableFields.has('productName')) return false
    return true
  })
  const alwaysVisibleColumns = visibleColumns.filter(col => ALWAYS_VISIBLE_IDS.includes(col.id))
  const modalColumns = visibleColumns.filter(col => !ALWAYS_VISIBLE_IDS.includes(col.id))

  const isNew = !row?.PK

  return (
    <>
      <ProductRowContainer container isNew={isNew}>
        {/* Drag Handle */}
        {/* {isDraft && (
          <DragHandle className="drag-handle">
            <DragIcon />
          </DragHandle>
        )} */}

        {/* Row Number */}
        <RowNumberBadge>{index + 1}</RowNumberBadge>

        {/* Columns */}
        {isSmallScreen ? (
          <>
            {/* Small screen: show only essential fields */}
            {alwaysVisibleColumns.map(column => {
              const sizes = getGridSizes(column)
              return (
                <CellWrapper item xs={sizes.xs} sm={sizes.sm} md={sizes.md} lg={sizes.md} xl={sizes.xl} key={column.id}>
                  <NewRenderCell
                    row={row}
                    column={column}
                    index={index}
                    handleProductChange={handleProductChange}
                    validationErrors={validationErrors}
                    handleRemoveRow={handleRemoveRow}
                    isDraft={isDraft}
                    customByServiceKey={customByServiceKey}
                    editableFields={editableFields}
                  />
                </CellWrapper>
              )
            })}

            {/* Edit button with tooltip preview */}
            <CellWrapper item xs={2} sm={2} md={2}>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    {modalColumns.map(column => (
                      <ServiceDetails key={column.id} column={column} value={row[column.id]} />
                    ))}
                  </Box>
                }
                arrow
                placement="bottom-start"
              >
                <Button variant="outlined" size="small" onClick={() => setIsModalOpen(true)}>
                  Edit
                </Button>
              </Tooltip>
            </CellWrapper>
          </>
        ) : (
          <>
            {/* Large screen: show all fields */}
            {visibleColumns.map(column => {
              const sizes = getGridSizes(column)
              return (
                <CellWrapper item xs={sizes.xs} sm={sizes.sm} md={sizes.md} lg={sizes.lg} xl={sizes.xl} key={column.id}>
                  <NewRenderCell
                    row={row}
                    column={column}
                    index={index}
                    handleProductChange={handleProductChange}
                    validationErrors={validationErrors}
                    handleRemoveRow={handleRemoveRow}
                    isDraft={isDraft}
                    customByServiceKey={customByServiceKey}
                    editableFields={editableFields}
                  />
                </CellWrapper>
              )
            })}
          </>
        )}
      </ProductRowContainer>

      {/* Modal for additional fields on small screens */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Edit Service Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {modalColumns.map(column => (
              <Grid item xs={12} key={column.id}>
                <NewRenderCell
                  row={row}
                  column={column}
                  index={index}
                  handleProductChange={handleProductChange}
                  validationErrors={validationErrors}
                  handleRemoveRow={handleRemoveRow}
                  isDraft={isDraft}
                  editableFields={editableFields}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsModalOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ProductRow

// /**
//  * ProductRow - Clean, refactored product row component
//  *
//  * Replaces the duplicated column rendering logic in RenderRow with a simple
//  * column-driven approach. Each column type is rendered once via a lookup.
//  * Uses centralized styles from styles/utilities/view-agreement.ts
//  */

// import {
//   Box,
//   Button,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Grid,
//   Tooltip,
//   useMediaQuery,
//   useTheme,
// } from '@mui/material'
// import React, { useState } from 'react'
// import NewRenderCell from '../Deals/grid/cells/NewRenderCell'
// import {
//   COMPACT_BREAKPOINT,
//   CellWrapper,
//   DragHandle,
//   DragIcon,
//   ProductRowContainer,
//   RowNumberBadge,
//   getGridSizes,
// } from '../ProductRows/ProductTableStyles'
// import { columns, csaColumns, ioColumns } from './columns'
// import ServiceDetails from './ServiceDetails'

// // Column IDs that should be visible on small screens
// const ALWAYS_VISIBLE_IDS = ['productName', 'description', 'price', 'deleteButton']

// // Column IDs that should render (excludes internal fields)
// const SKIP_COLUMN_IDS = ['rowId']

// interface ProductRowProps {
//   row: any
//   index: number
//   handleProductChange: (index: number, field: string, value: any, extra?: any) => void
//   provided: any
//   snapshot: any
//   handleRemoveRow: (sk: string) => void
//   validationErrors: any
//   type: string
//   isDraft: boolean
//   customByServiceKey?: Map<string, { price: number; margin: number }>
// }

// const ProductRow: React.FC<ProductRowProps> = ({
//   row,
//   index,
//   handleProductChange,
//   provided,
//   snapshot,
//   handleRemoveRow,
//   validationErrors,
//   type,
//   isDraft,
//   customByServiceKey,
// }) => {
//   const theme = useTheme()
//   // Use centralized breakpoint for compact view threshold
//   const isSmallScreen = useMediaQuery(theme.breakpoints.down(COMPACT_BREAKPOINT))
//   const [isModalOpen, setIsModalOpen] = useState(false)

//   // Select columns based on deal type
//   const csaTypes = ['CSA', 'RCSA']
//   const ioTypes = ['IO']
//   const rowColumns = csaTypes.includes(type) ? csaColumns : ioTypes.includes(type) ? ioColumns : columns

//   // Filter columns for visibility
//   const visibleColumns = rowColumns.filter(col => !SKIP_COLUMN_IDS.includes(col.id))
//   const alwaysVisibleColumns = visibleColumns.filter(col => ALWAYS_VISIBLE_IDS.includes(col.id))
//   const modalColumns = visibleColumns.filter(col => !ALWAYS_VISIBLE_IDS.includes(col.id))

//   const isNew = !row?.PK

//   return (
//     <>
//       <ProductRowContainer
//         container
//         isDragging={snapshot.isDragging}
//         isNew={isNew}
//         ref={provided.innerRef}
//         {...provided.draggableProps}
//       >
//         {/* Drag Handle */}
//         {isDraft && (
//           <DragHandle className="drag-handle" {...provided.dragHandleProps}>
//             <DragIcon />
//           </DragHandle>
//         )}

//         {/* Row Number */}
//         <RowNumberBadge>{index + 1}</RowNumberBadge>

//         {/* Columns */}
//         {isSmallScreen ? (
//           <>
//             {/* Small screen: show only essential fields */}
//             {alwaysVisibleColumns.map(column => {
//               const sizes = getGridSizes(column)
//               return (
//                 <CellWrapper item xs={sizes.xs} sm={sizes.sm} md={sizes.md} lg={sizes.md} xl={sizes.xl} key={column.id}>
//                   <NewRenderCell
//                     row={row}
//                     column={column}
//                     index={index}
//                     handleProductChange={handleProductChange}
//                     validationErrors={validationErrors}
//                     handleRemoveRow={handleRemoveRow}
//                     snapshot={snapshot}
//                     isDraft={isDraft}
//                     customByServiceKey={customByServiceKey}
//                   />
//                 </CellWrapper>
//               )
//             })}

//             {/* Edit button with tooltip preview */}
//             <CellWrapper item xs={2} sm={2} md={2}>
//               <Tooltip
//                 title={
//                   <Box sx={{ p: 1 }}>
//                     {modalColumns.map(column => (
//                       <ServiceDetails key={column.id} column={column} value={row[column.id]} />
//                     ))}
//                   </Box>
//                 }
//                 arrow
//                 placement="bottom-start"
//               >
//                 <Button variant="outlined" size="small" onClick={() => setIsModalOpen(true)}>
//                   Edit
//                 </Button>
//               </Tooltip>
//             </CellWrapper>
//           </>
//         ) : (
//           <>
//             {/* Large screen: show all fields */}
//             {visibleColumns.map(column => {
//               const sizes = getGridSizes(column)
//               return (
//                 <CellWrapper item xs={sizes.xs} sm={sizes.sm} md={sizes.md} lg={sizes.lg} xl={sizes.xl} key={column.id}>
//                   <NewRenderCell
//                     row={row}
//                     column={column}
//                     index={index}
//                     handleProductChange={handleProductChange}
//                     validationErrors={validationErrors}
//                     handleRemoveRow={handleRemoveRow}
//                     snapshot={snapshot}
//                     isDraft={isDraft}
//                     customByServiceKey={customByServiceKey}
//                   />
//                 </CellWrapper>
//               )
//             })}
//           </>
//         )}
//       </ProductRowContainer>

//       {/* Modal for additional fields on small screens */}
//       <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} fullWidth maxWidth="sm">
//         <DialogTitle sx={{ pb: 1 }}>Edit Service Details</DialogTitle>
//         <DialogContent>
//           <Grid container spacing={2} sx={{ mt: 0.5 }}>
//             {modalColumns.map(column => (
//               <Grid item xs={12} key={column.id}>
//                 <NewRenderCell
//                   row={row}
//                   column={column}
//                   index={index}
//                   handleProductChange={handleProductChange}
//                   validationErrors={validationErrors}
//                   handleRemoveRow={handleRemoveRow}
//                   snapshot={snapshot}
//                   isDraft={isDraft}
//                 />
//               </Grid>
//             ))}
//           </Grid>
//         </DialogContent>
//         <DialogActions sx={{ px: 3, pb: 2 }}>
//           <Button onClick={() => setIsModalOpen(false)} variant="contained">
//             Done
//           </Button>
//         </DialogActions>
//       </Dialog>
//     </>
//   )
// }

// export default ProductRow
